import FitKeywords from "@/components/common/FitKeywords";
import CompanyLink from "@/components/common/CompanyLink";
import CompanyLogo from "@/components/common/CompanyLogo";
import KeywordMatch from "@/components/common/KeywordMatch";
import { actionProps } from "@/lib/controlAnalytics";
import SourceTag from "@/components/common/SourceTag";
import { PinIcon } from "@/components/icons";
import { StatusPill } from "@/components/posting/parts";
import type { JobCardView } from "@/lib/jobs";
import { postingDetailPath } from "@/lib/routes";
import type { ReactNode } from "react";

/**
 * One result: the title, the company, where, the pay it states, the start of
 * the text, when it was posted, found and last seen, and the fit or what
 * was done. The title is a link to the posting's own page, so a search
 * engine can follow it. A plain press opens the posting in the drawer
 * beside the list instead, or on its own page on mobile. The company is a
 * link to every job it has. A footer, when given, sits at the bottom, so a
 * row of cards lines its buttons up.
 */
export default function JobCard({
    card,
    selected,
    onOpen,
    actions,
    footer,
    member = true,
    linkCompany = true,
}: {
    card: JobCardView;
    selected: boolean;
    onOpen: () => void;
    /** Save, beside the dates: at full contrast for everyone. */
    actions?: ReactNode;
    /** What the person can do with it, under a line at the card's foot. */
    footer?: ReactNode;
    /** Signed in: where the application stands, and the fit. */
    member?: boolean;
    /** The company leads to its jobs. Off on that company's own page. */
    linkCompany?: boolean;
}) {
    return (
        <article
            {...actionProps("posting_drawer_opened", {
                source: "JobCard",
                posting_id: card.postingId,
            })}
            className={`flex cursor-pointer flex-col gap-2.5 rounded-card px-[18px] py-4 text-fg shadow-card transition-colors ${
                selected
                    ? "border border-transparent bg-card ring-2 ring-[rgba(33,212,92,.55)] dark:ring-[rgba(63,224,122,.6)]"
                    : "border border-line bg-card hover:border-line-strong"
            }`}
            onClick={(event) => {
                if (
                    event.target instanceof Element &&
                    event.target.closest("[data-keyword-details], [data-card-actions]")
                )
                    return;
                onOpen();
            }}
        >
            {/* On a phone the status takes its own row, so the logo leaves the text room. */}
            <div className="flex items-start justify-between gap-3 max-[760px]:grid max-[760px]:grid-cols-[minmax(0,1fr)_auto] max-[760px]:gap-y-2">
                <div className="flex min-w-0 flex-col gap-[3px]">
                    <a
                        {...actionProps("posting_drawer_opened", {
                            source: "JobCard",
                            posting_id: card.postingId,
                        })}
                        href={postingDetailPath(card.postingId)}
                        onClick={(event) => {
                            // Not the card's own press: that would open the drawer again.
                            event.stopPropagation();
                            // A new tab, a new window or a download is the browser's.
                            if (
                                event.button !== 0 ||
                                event.metaKey ||
                                event.ctrlKey ||
                                event.shiftKey ||
                                event.altKey
                            )
                                return;
                            event.preventDefault();
                            onOpen();
                        }}
                        aria-current={selected ? "true" : undefined}
                        className="text-left text-[14.5px] font-semibold leading-[1.3] hover:text-brand-ink"
                    >
                        {card.title}
                    </a>
                    <span className="flex flex-wrap items-center gap-2 text-[12.5px] text-fg-muted">
                        {linkCompany ? (
                            <CompanyLink
                                company={card.company}
                                logoUrl={card.logoUrl}
                                source={card.source}
                                board={card.board}
                                from="JobCard"
                                className="font-medium text-fg"
                            />
                        ) : (
                            <span className="font-medium text-fg">{card.company}</span>
                        )}
                        <SourceTag source={card.source} />
                        {card.where && (
                            <span className="inline-flex items-center gap-1">
                                <PinIcon className="size-3 text-fg-faint" />
                                {card.where}
                            </span>
                        )}
                        {card.pay && (
                            <span
                                data-testid="card-pay"
                                className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-pay-line bg-pay px-2 py-px font-mono text-[11.5px] font-semibold text-brand-ink"
                            >
                                {card.pay}
                            </span>
                        )}
                    </span>
                </div>
                <div className="flex shrink-0 items-start gap-2.5 max-[760px]:contents">
                    {member && (
                        <div className="max-[760px]:col-span-2 max-[760px]:row-start-2">
                            <StatusPill label={card.chip.label} tone={card.chip.tone} size="sm" />
                        </div>
                    )}
                    {/* Room for the widest logo, so the text stays put as one loads. */}
                    <div
                        className={
                            card.logoUrl
                                ? "flex h-9 w-[90px] justify-end max-[760px]:w-[72px]"
                                : "contents"
                        }
                    >
                        <CompanyLogo url={card.logoUrl} company={card.company} />
                    </div>
                </div>
            </div>
            {card.excerpt && (
                <p className="m-0 text-[12.5px] leading-normal text-fg-muted [text-wrap:pretty]">
                    {card.excerpt}
                </p>
            )}
            {member && card.fit && card.fit.asked > 0 && <FitKeywords fit={card.fit} />}
            {/* One line on a phone: "Updated" gives way, and the date shrinks before Save drops. */}
            <div className="flex flex-wrap items-center gap-3 text-[11.5px] text-fg-faint max-[760px]:flex-nowrap">
                {card.posted && (
                    <span className="min-w-0 truncate text-fg-muted">Posted {card.posted}</span>
                )}
                <span className="max-[760px]:hidden">Updated {card.updated}</span>
                {member && card.aside && (
                    <span className="ml-auto shrink-0 whitespace-nowrap font-mono text-[12px] font-semibold text-brand-ink">
                        {card.fit && card.fit.asked > 0 ? (
                            <KeywordMatch fit={card.fit}>{card.aside}</KeywordMatch>
                        ) : (
                            card.aside
                        )}
                    </span>
                )}
                {actions && (
                    <span
                        data-card-actions
                        className={`shrink-0 ${member && card.aside ? "" : "ml-auto"}`}
                    >
                        {actions}
                    </span>
                )}
            </div>
            {footer && (
                <div
                    data-card-actions
                    className="mt-auto flex flex-wrap items-center gap-2 border-t border-line-soft pt-2.5"
                >
                    {footer}
                </div>
            )}
        </article>
    );
}
